/**
 * Location primitives — Deep-Linked State (design README §3: every screen
 * with its filters — deployment, TPIN/branch, period — is a URL; Back/
 * Forward, bookmarks and refresh restore the exact state).
 *
 * The URL is the single source of truth for navigation + period + filter.
 * A Link or selector change pushes history and dispatches popstate;
 * useRoute re-reads.
 *
 * C1a extension (SIH-4 / FR-2): the portfolio view now filters by
 * deployment or TPIN — carried as `?deployment={TPIN_branch}` or
 * `?tpin={TPIN}` query parameters, additively. Existing URLs (no query
 * params, `/deployment/{TPIN_branch}`, `?period=…`) keep working.
 *
 * v1.1 (SIH-17) — additive routes:
 * - `/tpin/{tin}` → TPIN drill view (C1b, proposal §3.9). Grammar is
 *   additive — the existing `/deployment/{tinBranch}` regex stays; the
 *   new regex matches the deeper prefix `/tpin/{numeric-tin}` only.
 * - `/deployment/{tinBranch}/pending` → Pending list (FR-4, proposal
 *   §3.10). Also additive — the suffix `/pending` distinguishes it
 *   from the Failed-list view that owns the bare `/deployment/{tinBranch}`.
 */

import { useCallback } from 'react'
import { useSyncExternalStore } from 'react'
import { resolvePeriod, validateCustomRange } from '@domain/period'
import type { PeriodSelection } from '@domain/types'

export interface Route {
  /** null = portfolio home; otherwise a deployment {TPIN}_{branch} path. */
  readonly deployment: string | null
  /** true when the deployment route is the pending list sub-path. */
  readonly pendingList: boolean
  /** true when the deployment route is the failed list (default). */
  readonly failedList: boolean
  /** TPIN drill view — null = not on `/tpin/{tin}`; otherwise the TPIN. */
  readonly tpin: string | null
  /**
   * Raw `?deployment=` value (not format-validated). The view checks
   * format + enumeration — invalid format AND unknown-but-valid format
   * both render the named invalid-selection state (FR-2 fit criterion:
   * invalid/unknown URL selections render a real error state, never blank).
   * null when the param is absent.
   */
  readonly portfolioDeploymentRaw: string | null
  /** Raw `?tpin=` value (not format-validated). Same rule as above. */
  readonly portfolioTpinRaw: string | null
  /** Period from the URL (?period=…); defaults to TODAY when absent. */
  readonly period: PeriodSelection
}

const PRESETS = new Set(['TODAY', 'LAST_7_DAYS', 'LAST_30_DAYS', 'CUSTOM'])
const TPIN_BRANCH_RE = /^\/deployment\/([0-9]+_[0-9]+)(\/pending)?\/?$/
const TPIN_ROOT_RE = /^\/tpin\/([0-9]+)\/?$/

function parsePeriod(params: URLSearchParams): PeriodSelection {
  const period = params.get('period')
  if (period === 'CUSTOM') {
    return { preset: 'CUSTOM', from: params.get('from') ?? '', to: params.get('to') ?? '' }
  }
  if (period !== null && PRESETS.has(period)) {
    return { preset: period as Exclude<PeriodSelection['preset'], 'CUSTOM'> }
  }
  return { preset: 'TODAY' }
}

function parseQueryFilter(
  params: URLSearchParams,
): { portfolioDeploymentRaw: string | null; portfolioTpinRaw: string | null } {
  return {
    portfolioDeploymentRaw: params.get('deployment'),
    portfolioTpinRaw: params.get('tpin'),
  }
}

function parseLocation(): Route {
  const path = window.location.pathname
  const params = new URLSearchParams(window.location.search)
  const filter = parseQueryFilter(params)

  // TPIN drill wins when the path matches; it's the most specific match.
  const tpinMatch = TPIN_ROOT_RE.exec(path)
  if (tpinMatch !== null) {
    return {
      deployment: null,
      pendingList: false,
      failedList: false,
      tpin: decodeURIComponent(tpinMatch[1] as string),
      portfolioDeploymentRaw: filter.portfolioDeploymentRaw,
      portfolioTpinRaw: filter.portfolioTpinRaw,
      period: parsePeriod(params),
    }
  }

  const deploymentMatch = TPIN_BRANCH_RE.exec(path)
  if (deploymentMatch !== null) {
    const pending = deploymentMatch[2] !== undefined
    return {
      deployment: decodeURIComponent(deploymentMatch[1] as string),
      pendingList: pending,
      failedList: !pending,
      tpin: null,
      portfolioDeploymentRaw: filter.portfolioDeploymentRaw,
      portfolioTpinRaw: filter.portfolioTpinRaw,
      period: parsePeriod(params),
    }
  }

  return {
    deployment: null,
    pendingList: false,
    failedList: false,
    tpin: null,
    portfolioDeploymentRaw: filter.portfolioDeploymentRaw,
    portfolioTpinRaw: filter.portfolioTpinRaw,
    period: parsePeriod(params),
  }
}

// useSyncExternalStore requires a CACHED snapshot: a fresh object per call
// makes React believe the store changed on every check → render loop.
let lastKey: string | null = null
let lastRoute: Route = {
  deployment: null,
  pendingList: false,
  failedList: false,
  tpin: null,
  portfolioDeploymentRaw: null,
  portfolioTpinRaw: null,
  period: { preset: 'TODAY' },
}

function getSnapshot(): Route {
  const key = `${window.location.pathname}?${window.location.search}`
  if (key !== lastKey) {
    lastKey = key
    lastRoute = parseLocation()
  }
  return lastRoute
}

function subscribe(callback: () => void): () => void {
  window.addEventListener('popstate', callback)
  return () => window.removeEventListener('popstate', callback)
}

export function useRoute(): Route {
  return useSyncExternalStore(subscribe, getSnapshot, () => lastRoute)
}

/** Query-string fragment carrying the period (shared by every internal link). */
export function periodSearchParams(period: PeriodSelection): string {
  const params = new URLSearchParams()
  params.set('period', period.preset)
  if (period.preset === 'CUSTOM') {
    params.set('from', period.from ?? '')
    params.set('to', period.to ?? '')
  }
  return params.toString()
}

/**
 * Compose the full query string for the portfolio home — period + the
 * optional portfolio-level filter (TPIN or branch).
 */
export function portfolioSearchParams(
  period: PeriodSelection,
  filter: { deployment: string | null; tpin: string | null },
): string {
  const params = new URLSearchParams()
  params.set('period', period.preset)
  if (period.preset === 'CUSTOM') {
    params.set('from', period.from ?? '')
    params.set('to', period.to ?? '')
  }
  if (filter.deployment !== null) params.set('deployment', filter.deployment)
  if (filter.tpin !== null) params.set('tpin', filter.tpin)
  return params.toString()
}

/** Compose a path + current period into a deep-linked href. */
export function hrefFor(pathname: string, period: PeriodSelection): string {
  // The query always carries at least `period=`, so it is never empty.
  return `${pathname}?${periodSearchParams(period)}`
}

/** Compose the portfolio home href with the current filter state (deep-link). */
export function portfolioHrefFor(
  period: PeriodSelection,
  filter: { deployment: string | null; tpin: string | null },
): string {
  // The query always carries at least `period=`, so it is never empty.
  return `/?${portfolioSearchParams(period, filter)}`
}

/**
 * Push a new location (path + period) onto history and notify subscribers —
 * the single navigation primitive for links and the period selector alike.
 */
export function navigateTo(pathname: string, period: PeriodSelection): void {
  window.history.pushState({}, '', hrefFor(pathname, period))
  window.dispatchEvent(new PopStateEvent('popstate'))
}

/**
 * Push a new portfolio location (path + period + filter) onto history.
 * The path is always `/` — the filter is encoded in the query string.
 */
export function navigateToPortfolio(
  period: PeriodSelection,
  filter: { deployment: string | null; tpin: string | null },
): void {
  window.history.pushState({}, '', portfolioHrefFor(period, filter))
  window.dispatchEvent(new PopStateEvent('popstate'))
}

/**
 * A URL-carried period is only usable when a custom range is valid
 * (FR-5: from ≤ to). Invalid bookmarked ranges render the real
 * invalid-range state — never queried, never blank.
 */
export function routePeriodUsable(period: PeriodSelection): { usable: true; resolved: { from: string; to: string } } | { usable: false; reason: string } {
  switch (period.preset) {
    case 'TODAY':
    case 'LAST_7_DAYS':
    case 'LAST_30_DAYS': {
      return { usable: true, resolved: resolvePeriod({ preset: period.preset }) }
    }
    case 'CUSTOM': {
      const from = period.from ?? ''
      const to = period.to ?? ''
      const verdict = validateCustomRange(from, to)
      if (!verdict.ok) return { usable: false, reason: verdict.reason }
      return { usable: true, resolved: { from, to } }
    }
  }
}

/** Internal anchor honouring Deep-Linked State (intercept same-app clicks). */
export function useNavigate(): (event: React.MouseEvent<HTMLAnchorElement>, pathname: string, period: PeriodSelection) => void {
  return useCallback((event, pathname, period) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    event.preventDefault()
    navigateTo(pathname, period)
  }, [])
}